/**
 * What a running job's numbers mean to a screen drawing a bar.
 *
 * Both places that draw one — the button on a game page and the row on the downloads screen —
 * were reading the numbers off the wire with `??`, and that is the wrong operator for these.
 * Every phase opens by reporting nought of nought, because it is about to start measuring a
 * quantity the phase before it was not counting. Nought is a number, so `??` kept it, and a
 * freshly started "Downloading" drew a dead bar with no size, no speed and no estimate beside
 * it until a whole chunk had landed.
 *
 * So the rule is stated once, here, and tested: a pair is only a measurement when its total is
 * positive, the newer source wins when both are, and when neither has measured anything the
 * bar is told to say so rather than to draw a zero it cannot stand behind.
 */

import type { JobProgress, QueuedJob } from '../platform/tauri.js';

export interface DownloadNumbers {
  /** Bytes measured in the phase the job is in, or null while it has measured nothing. */
  done: number | null;
  total: number | null;
  speedBps: number;
  etaS: number | null;
  /**
   * What the bar draws: 0 to 1, or null for a bar with no figure to give — which is a
   * different thing from nought per cent, and is drawn differently.
   */
  fraction: number | null;
  /** Whole percent, for the label. Null whenever `fraction` is. */
  percent: number | null;
  /**
   * The size to name when there is no measurement yet.
   *
   * The build's own size, which is known from the moment the job is queued. It is deliberately
   * kept apart from `total`: a download measures compressed bytes off the network, and dividing
   * those by the size of the installed game would draw a bar that never reaches the end.
   */
  size: number | null;
}

const NOTHING_MEASURED: Omit<DownloadNumbers, 'size'> = {
  done: null,
  total: null,
  speedBps: 0,
  etaS: null,
  fraction: null,
  percent: null,
};

/**
 * Reads one job's figures.
 *
 * `progress` is the event stream — nothing until the first event of this session arrives, which
 * on a screen opened mid-download is a while. `queued` is the native queue, which can be asked
 * at any moment and is how such a screen draws anything at all. Either may be absent.
 */
export function downloadNumbers(
  progress: JobProgress | null,
  queued: QueuedJob | null,
): DownloadNumbers {
  const size = queued?.buildBytes ?? null;

  // The pair is taken from one source rather than field by field, because the two sources can
  // be a phase apart: a `bytesDone` counting bytes written to disk over a `bytesTotal` counting
  // bytes off the network is a percentage of nothing.
  const source =
    progress && progress.bytesTotal > 0
      ? progress
      : queued && queued.bytesTotal > 0
        ? queued
        : null;

  if (!source) return { ...NOTHING_MEASURED, size };

  const total = source.bytesTotal;
  // A retry that gave bytes back, or a phase whose total shrank after replanning, must not draw
  // past the end of the track.
  const done = Math.max(0, Math.min(source.bytesDone, total));
  const fraction = done / total;

  return {
    done,
    total,
    speedBps: Math.max(0, source.speedBps),
    etaS: source.etaS,
    fraction,
    percent: Math.min(100, Math.round(fraction * 100)),
    size,
  };
}

/** The line of figures under a bar: "4.2 GB / 30.1 GB · 62 MB/s · 7 min left". */
export function progressSummary(
  numbers: DownloadNumbers,
  format: {
    bytes: (n: number) => string;
    speed: (n: number) => string;
    duration: (s: number) => string;
  },
): string {
  const parts: string[] = [];
  if (numbers.total !== null && numbers.done !== null) {
    parts.push(`${format.bytes(numbers.done)} / ${format.bytes(numbers.total)}`);
  } else if (numbers.size !== null && numbers.size > 0) {
    // Nothing measured yet, but the size of the build has been known since it was queued, and
    // an empty line where the figures go reads as a job that is not really running.
    parts.push(format.bytes(numbers.size));
  }
  if (numbers.speedBps > 0) parts.push(`${format.speed(numbers.speedBps)}/s`);
  if (numbers.etaS) parts.push(`${format.duration(numbers.etaS)} left`);
  return parts.join(' · ');
}
